import React, { useMemo } from "react";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faBadgeCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { throttle } from "lodash";
import moment from "moment";
import useResizeObserver from "use-resize-observer";

import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import type { Platform } from "~/lib/prose/plugins/multicursor";
import { decodeGlobalId } from "~/lib/relayUtil";
import { str2AttendeeColors } from "~/lib/utils";
import type { UserType } from "~/types/main.d";
import { colors as uiColors } from "~/ui";
import Avatar, { type UserAttrs } from "~/ui/legacy/Avatar";
import Popup from "~/ui/legacy/Popup";
import { IconButton } from "~/ui/legacyFellowIndex";

import styles from "./css/Attendees.module.css";

declare module "csstype" {
	interface Properties {
		/** Our CSS variables */
		"--attendee-ring-color"?: string;
		"--attendee-tooltip-color"?: string;
	}
}

interface AttendeeSession {
	sessionId: string;
	isCurrentlyViewing: boolean;
	lastActiveDatetime?: string;
	platform: Platform;
}

export interface AttendeesPresenceInfo {
	// The key is the user's id as sent by ProseMultiCursorHandler
	[userId: string]: AttendeeSession[];
}

export interface AttendeeAttrs {
	__typename: string;
	id: string;
	type: UserType;
	email: string;
	invitable: boolean;
	fullName: string;
	image: string;
	user: UserAttrs | undefined;
	lastSeenAt?: string | null;
	isConfigured?: boolean;
	isGuest: boolean;
	isAnonymous?: boolean;
	isExternal: boolean;
	responseStatus?: string;
	title?: string;
	isOptional?: boolean;
	isOrganizer?: boolean;
	isExternalInNote?: boolean;
}

interface Props {
	attendees: AttendeeAttrs[];
	presenceInfo: AttendeesPresenceInfo;
	maxLength: number;
	className?: string;
	onOpenShareModal?: () => void;
	onOpenSharedWithModal?: () => void;
	avatarSize?: string; // TODO: rename into "size", make it a number and use a pixel value everywhere
	sizeInPixels?: number; // TODO: ideally, should be based on avatarSize and convert it to pixels
}

function Attendees({
	attendees,
	presenceInfo,
	maxLength,
	className,
	onOpenShareModal,
	onOpenSharedWithModal,
	avatarSize,
	sizeInPixels,
}: Props) {
	const [numAttendees, setNumAttendees] = React.useState(maxLength);
	const size = avatarSize ?? "2em";

	const { ref } = useResizeObserver<HTMLDivElement>({
		onResize: useMemo(
			() =>
				throttle(({ width }) => {
					// considering each Avatar width is 32px (minus 4px for overlapping)
					// we want to render the amount avatars that fit in `ref`
					if (!width) return;

					const avatarSizeInPixels = sizeInPixels ?? 28;
					// -1 to give space to `others` avatar, if present
					const nextValue = Math.min(maxLength, Math.max(2, Math.floor((width - 4) / avatarSizeInPixels))) - 1;

					if (nextValue !== numAttendees) {
						setNumAttendees(nextValue);
					}
				}, 200),
			[maxLength, sizeInPixels, numAttendees, setNumAttendees],
		),
	});

	const { user: contextUser } = React.useContext(AppContext);
	const { isMobile } = React.useContext(ViewportContext);

	const isAttendeeCurrentlyViewing = (user?: UserAttrs) => {
		if (!user) return false;
		const userId = user.isAnonymous ? user.id : decodeGlobalId(user.id)[1];
		return (presenceInfo[userId] ?? []).some(s => s.isCurrentlyViewing);
	};

	const getAttendeeLastActiveDatetime = (user?: UserAttrs) => {
		if (!user) return undefined;
		const userId = user.isAnonymous ? user.id : decodeGlobalId(user.id)[1];
		const session = (presenceInfo[userId] ?? []).find(s => !s.isCurrentlyViewing);
		return session?.lastActiveDatetime;
	};

	const renderAttendee = (attendee: AttendeeAttrs, withName?: boolean) => {
		const { lastSeenAt, email, fullName, user, isAnonymous, isExternal } = attendee;

		// Include ID in key for users in case there are users from multiple
		// accounts with the same email.
		const key = user ? `attendee-${email}-${user.id}` : `attendee-${email}`;
		const colors = str2AttendeeColors(fullName);
		const self = user?.id === contextUser?.id;
		const isCurrentlyViewing = self || isAttendeeCurrentlyViewing(attendee.user);
		const lastActiveDatetime = getAttendeeLastActiveDatetime(attendee.user) ?? lastSeenAt;

		const styleColors = {
			"--attendee-ring-color": isCurrentlyViewing ? `#${colors.ring}` : uiColors.grey[800],
			"--attendee-tooltip-color": isCurrentlyViewing ? `#${colors.tooltip}` : uiColors.grey[700],
		};

		const uiAvatarOptions = {
			background: uiColors.grey[400].substring(1),
		};

		const avatarWrapper = (
			<div
				data-tour-tooltip-target="attendees"
				key={key}
				className={cx(styles.avatar, {
					[styles.avatarViewing]: isCurrentlyViewing,
					[styles.avatarClickable]: !!onOpenSharedWithModal,
				})}
				style={{ ...styleColors, height: size }}
				onClick={onOpenSharedWithModal}
			>
				<Avatar
					user={attendee}
					width={size}
					link={false}
					useTitle={false}
					uiAvatarOptions={isCurrentlyViewing ? undefined : uiAvatarOptions}
				/>
			</div>
		);

		if (isMobile) {
			return avatarWrapper;
		}

		const trigger = withName ? (
			<div className={styles.attendee}>
				{avatarWrapper}
				<p className={styles.attendeeName}>{fullName}</p>
			</div>
		) : (
			avatarWrapper
		);

		const popupContent = (
			<>
				<p className={styles.popupAttendeeName}>
					{fullName} {self && "(Me)"}
				</p>
				{/*
					Anonymous users will be external when coming from note stream invites.
					In that case we kinda know who they are so we don't show them as anonymous
				*/}
				{isAnonymous && !isExternal ? (
					<p className={styles.popupAttendeeLastSeen}>Anonymous viewer</p>
				) : isCurrentlyViewing ? (
					<p className={styles.popupAttendeeLastSeen}>Currently viewing</p>
				) : lastActiveDatetime ? (
					<p className={styles.popupAttendeeLastSeen}>Viewed {moment(lastActiveDatetime).fromNow()}</p>
				) : null}
				{user?.isTopUser && (
					<p className={styles.popupAttendeeLastSeen}>
						<FontAwesomeIcon icon={faBadgeCheck} /> Top Fellow User
					</p>
				)}
			</>
		);

		return (
			<Popup
				key={key}
				size="tiny"
				content={popupContent}
				position="bottom center"
				trigger={trigger}
				className={styles.popup}
				style={styleColors}
			/>
		);
	};

	const compareFn = (a: AttendeeAttrs, b: AttendeeAttrs) => {
		// End result should be something like:
		// [self, live activity, newer recency, ..., older recency]

		const aIsSelf = a.user?.id === contextUser?.id;
		const bIsSelf = b.user?.id === contextUser?.id;

		if (aIsSelf && !bIsSelf) return -1;
		if (!aIsSelf && bIsSelf) return 1;

		const aIsCurrentlyViewing = isAttendeeCurrentlyViewing(a.user);
		const bIsCurrentlyViewing = isAttendeeCurrentlyViewing(b.user);

		if (aIsCurrentlyViewing && !bIsCurrentlyViewing) return -1;
		if (!aIsCurrentlyViewing && bIsCurrentlyViewing) return 1;

		if (a.user?.isTopUser && !b.user?.isTopUser) return -1;
		if (!a.user?.isTopUser && b.user?.isTopUser) return 1;

		if (!a.isAnonymous && b.isAnonymous) return -1;
		if (a.isAnonymous && !b.isAnonymous) return 1;

		const aLastActiveDatetime = getAttendeeLastActiveDatetime(a.user) ?? a.lastSeenAt;
		const bLastActiveDatetime = getAttendeeLastActiveDatetime(b.user) ?? b.lastSeenAt;

		if (aLastActiveDatetime && !bLastActiveDatetime) return -1;
		if (!aLastActiveDatetime && bLastActiveDatetime) return 1;

		if (aLastActiveDatetime && bLastActiveDatetime) {
			return moment(bLastActiveDatetime).diff(moment(aLastActiveDatetime));
		}

		return 0;
	};

	const emailsWithUsers = new Set<string>(attendees.filter(a => !!a.user).map(a => a.email));

	// let's exclude isGuest attendees from this panel. These users have no access to the note
	const filteredAttendees = attendees.filter(
		attendee =>
			!(attendee.__typename === "Attendee" && (attendee.isGuest || attendee.isAnonymous)) &&
			(!emailsWithUsers.has(attendee.email) || !!attendee.user),
	);
	const sortedAttendees = filteredAttendees.sort(compareFn);
	const firstAttendees = sortedAttendees.slice(0, numAttendees).reverse();
	const othersAttendees = sortedAttendees.slice(numAttendees);
	const othersLength = othersAttendees.length;

	const othersAvatar = (
		<div className={cx(styles.avatar, styles.avatarClickable)} style={{ height: size }} onClick={onOpenSharedWithModal}>
			<Avatar
				width={size}
				link={false}
				useTitle={false}
				uiAvatarOptions={{ background: uiColors.grey[200].substring(1), color: uiColors.grey[600].substring(1) }}
				initials={othersLength}
			/>
		</div>
	);

	const popupTrigger = React.useMemo(
		() => (
			<IconButton
				icon={faPlus}
				className={styles.plusButton}
				onClick={onOpenShareModal}
				style={{ width: size, height: size }}
			/>
		),
		[onOpenShareModal, size],
	);

	return (
		<div ref={ref} className={cx(styles.container, className)} style={{ "--Attendees__avatarSize": size }}>
			{!!onOpenShareModal && (
				<Popup
					size="tiny"
					content="Share"
					position="bottom center"
					trigger={popupTrigger}
					className={styles.shareTooltip}
				/>
			)}
			{othersLength > 0 && (othersLength === 1 ? renderAttendee(othersAttendees[0]) : othersAvatar)}
			{firstAttendees.map(attendee => renderAttendee(attendee))}
		</div>
	);
}

export default Attendees;
